import React, {useEffect, useImperativeHandle, useRef, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {
    AspectRatio,
    Flex,
    Img,
    List,
    ListItem,
    useMediaQuery,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {remCalc} from '../../../theme/_utils'
import {ChevronUp, ChevronDown, Icon360, PlayIcon} from '../../icons'
import ProductThumbnail from '../../product-thumbnail'
import ImageGalleryDivider from '../../dividers/image-gallery-divider'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {handleChevronStyles} from './utils'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {parseAndSanitizeJSON} from '../../../utils/utils'
import {BRAND_KEY} from '../../../brand'
import {useIntl, defineMessages} from 'react-intl'

const messages = defineMessages({
    upArrow: {
        id: 'gallery-slider.up-arrow',
        defaultMessage: 'Scroll up thumbnails',
    },
    downArrow: {
        id: 'gallery-slider.down-arrow',
        defaultMessage: 'Scroll down thumbnails',
    },
    playVideo: {
        id: 'gallery-slider.play-video',
        defaultMessage: 'Play product video',
    },
    thumbnailsList: {
        id: 'gallery-slider.thumbnails-list',
        defaultMessage: 'Product thumbnails',
    },
    showMoreImages: {
        id: 'gallery-slider.show-more-images',
        defaultMessage: 'Show {count} more images',
    },
})

const GallerySlider = React.forwardRef(
    (
        {
            loadingStrategy,
            videoUrl,
            styles,
            selectedIndex,
            setSelectedIndex,
            scrollPosition,
            setScrollPosition,
            thumbnailImages,
            swiperRef,
            scrollRef,
            stopPropagation,
            openModal,
            setIsModalGallerySwiper = noop,
            isModalOpen,
        },
        ref,
    ) => {
        const imageRef = useRef(null)
        const imageRefScroll = imageRef && imageRef.current && imageRef.current.offsetHeight
        const [showAllModalThumbnails, setShowAllModalThumbnails] = useState(false)
        const modalThumbnailSize = remCalc(64)
        const thumbnailStyles = useMultiStyleConfig('ProductThumbnail')

        const intl = useIntl()

        const [isDesktop] = useMediaQuery(`(min-width: ${remCalc(1024)})`)
        const [isTablet] = useMediaQuery(`(min-width: ${remCalc(768)})`)
        const [isSmallScreen] = useMediaQuery(`(max-width: ${remCalc(767)})`)
        const useVerticalThumbnailAxis = isDesktop && !isModalOpen
        const videos = parseAndSanitizeJSON(videoUrl)?.filter((item) => item?.videolink) || []
        const video = videos[0]
        const videoIndex = thumbnailImages.length
        const normalizedThumbnailItems = useMemo(
            () =>
                thumbnailImages.map((image, index) => ({
                    ...image,
                    __itemIndex: index,
                })),
            [thumbnailImages],
        )
        const modalThumbnailItems = useMemo(() => {
            if (!isModalOpen || videos.length === 0) return normalizedThumbnailItems
            return [
                ...normalizedThumbnailItems,
                ...videos.map((videoItem, index) => ({
                    __isVideo: true,
                    __itemIndex: videoIndex + index,
                    alt: videoItem?.title,
                    title: videoItem?.title,
                })),
            ]
        }, [isModalOpen, normalizedThumbnailItems, videos, videoIndex])

        //* Placeholder variable for video & 360 icon
        const is360Enabled = false

        const handleVerticalScroll = (amount) => {
            if (!scrollRef.current) return
            if (useVerticalThumbnailAxis) {
                scrollRef.current.scrollTop += amount
                setScrollPosition(scrollRef.current.scrollTop)
                return
            }
            scrollRef.current.scrollLeft += amount
            setScrollPosition(scrollRef.current.scrollLeft)
        }

        let middlePosition, top, bottom, updatedScrollPosition

        if (useVerticalThumbnailAxis) {
            middlePosition = scrollRef?.current?.offsetHeight / 2
            top = scrollPosition <= 0
            bottom =
                scrollPosition >=
                scrollRef?.current?.scrollHeight - scrollRef?.current?.clientHeight
            updatedScrollPosition = top ? 'top' : bottom ? 'bottom' : 'between'
        } else {
            middlePosition = scrollRef?.current?.offsetWidth / 2
            top = scrollPosition <= 0
            bottom =
                scrollPosition >= scrollRef?.current?.scrollWidth - scrollRef?.current?.clientWidth
            updatedScrollPosition = top ? 'top' : bottom ? 'bottom' : 'between'
        }

        const shouldShowBlur =
            !(
                selectedIndex === thumbnailImages.length - 2 ||
                selectedIndex === thumbnailImages.length - 1
            ) &&
            !isDesktop &&
            !isModalOpen

        const m = isModalOpen ? remCalc(0) : remCalc(-32)
        const marginSliderContainer = {
            '@media (max-width: 1023px)': {
                margin: '0 auto',
            },
            '@media (min-width: 1024px)': {
                margin: isModalOpen ? '0 auto' : `${m} 0 0 0`,
            },
        }

        const modalVisibleSlots = isDesktop ? 9 : isTablet ? 7 : 4
        const sliderItems = isModalOpen ? modalThumbnailItems : normalizedThumbnailItems
        const showModalOverflowTile =
            isModalOpen &&
            !showAllModalThumbnails &&
            !isSmallScreen &&
            sliderItems.length > modalVisibleSlots
        const shouldCenterModalThumbnails = isModalOpen && !isSmallScreen
        const sliderWidth = isModalOpen ? '100%' : videoUrl ? `calc(100% - ${remCalc(84)})` : '100%'
        const visibleThumbnailImages = useMemo(() => {
            if (!showModalOverflowTile) return sliderItems

            const maxVisibleItems = modalVisibleSlots - 1
            return sliderItems.slice(0, maxVisibleItems)
        }, [sliderItems, showModalOverflowTile, modalVisibleSlots])
        const remainingModalThumbnails = Math.max(
            sliderItems.length - visibleThumbnailImages.length,
            0,
        )

        useEffect(() => {
            if (!showModalOverflowTile) return
            if (!Number.isInteger(selectedIndex) || selectedIndex < visibleThumbnailImages.length)
                return

            setShowAllModalThumbnails(true)
        }, [selectedIndex, showModalOverflowTile, visibleThumbnailImages.length])

        const handleSelection = (index) => {
            const gapWidth = isDesktop ? 8 : 16
            const newPos = index * gapWidth + (index + 0.5) * imageRefScroll - middlePosition
            // scroll horizzontally to newPosition
            if (useVerticalThumbnailAxis) {
                scrollRef.current.scrollTop = newPos
            } else {
                scrollRef.current.scrollLeft = newPos
            }

            setIsModalGallerySwiper(true)
            setScrollPosition(newPos)
            setSelectedIndex(index)
            stopPropagation()
            swiperRef && swiperRef.current && swiperRef.current.slideTo(index)
        }

        useImperativeHandle(ref, () => ({
            slideTo: (index) => {
                swiperRef.current?.slideTo(index, 0)
            },
        }))

        return (
            <Flex
                ref={ref}
                {...styles.sliderContainer}
                sx={{
                    ...marginSliderContainer,
                    ...(shouldCenterModalThumbnails
                        ? {
                              width: 'fit-content',
                              marginInline: 'auto',
                          }
                        : {}),
                }}
                data-ab={
                    !isModalOpen ? 'gallery-collapsed-miniatures' : 'gallery-exploded-miniatures'
                }
                data-cs-label={
                    !isModalOpen ? 'gallery-collapsed-miniatures' : 'gallery-exploded-miniatures'
                }
            >
                <Flex
                    as="button"
                    type="button"
                    {...styles.thumbnailImagesUpDownArrows}
                    aria-label={intl.formatMessage(messages.upArrow)}
                    tabIndex={0}
                    onClick={() => handleVerticalScroll(-(imageRefScroll / 2 + 8))}
                    {...handleChevronStyles(
                        'vertical',
                        thumbnailImages.length,
                        'top',
                        updatedScrollPosition,
                    )}
                    data-testid="thumbnail-up-arrow"
                >
                    <ChevronUp aria-hidden="true" />
                </Flex>
                <Flex
                    position={'relative'}
                    width={sliderWidth}
                    sx={
                        shouldCenterModalThumbnails
                            ? {
                                  marginInline: 'auto',
                                  justifyContent: 'center',
                              }
                            : {}
                    }
                >
                    <Flex
                        {...styles.sliderBlurredContainer}
                        sx={
                            shouldShowBlur
                                ? {
                                      _before: {
                                          content: '""',
                                          position: 'absolute',
                                          top: 0,
                                          bottom: 0,
                                          right: 0,
                                          bg: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)',
                                          width: '29%',
                                          pointerEvents: 'none',
                                          zIndex: 10,
                                      },
                                  }
                                : {}
                        }
                    >
                        <List
                            aria-label={intl.formatMessage(messages.thumbnailsList)}
                            {...styles.thumbnailImageList}
                            sx={{
                                '::-webkit-scrollbar': {display: 'none'},
                                msOverflowStyle: 'none',
                                scrollbarWidth: 'none',
                                ...(isModalOpen && showAllModalThumbnails
                                    ? {
                                          overflowX: 'auto',
                                          overflowY: 'hidden',
                                          maxHeight: `calc(${modalThumbnailSize} + ${remCalc(8)})`,
                                      }
                                    : {}),
                                ...(shouldCenterModalThumbnails
                                    ? {
                                          width: 'fit-content',
                                          marginInline: 'auto',
                                          justifyContent: 'flex-start',
                                      }
                                    : {}),
                                // reserve room for the active thumbnail's boxShadow ring so it isn't clipped by the scroll edge
                                ...(isModalOpen && selectedIndex === 0
                                    ? {paddingInlineStart: remCalc(4)}
                                    : {}),
                            }}
                            ref={scrollRef}
                        >
                            {visibleThumbnailImages.map((image, index) => {
                                const itemIndex =
                                    typeof image?.__itemIndex === 'number'
                                        ? image.__itemIndex
                                        : index

                                if (image?.__isVideo) {
                                    const videoItem = videos[itemIndex - videoIndex]
                                    const selected = selectedIndex === itemIndex

                                    return (
                                        <ListItem
                                            key="modal-video-thumbnail"
                                            height="fit-content"
                                            flexShrink={0}
                                            opacity={isModalOpen && !selected ? 0.6 : 1}
                                            sx={{_hover: {opacity: 1, cursor: 'pointer'}}}
                                        >
                                            <AspectRatio
                                                as="button"
                                                type="button"
                                                ratio={1}
                                                width={modalThumbnailSize}
                                                height={modalThumbnailSize}
                                                borderRadius="large"
                                                overflow="hidden"
                                                aria-label={intl.formatMessage(messages.playVideo)}
                                                data-testid="play-button-thumbnail"
                                                {...(selected
                                                    ? thumbnailStyles.thumbnailImageItemActive
                                                    : {})}
                                                onClick={() => {
                                                    setIsModalGallerySwiper(false)
                                                    setSelectedIndex(itemIndex)
                                                    stopPropagation()
                                                }}
                                            >
                                                <>
                                                    <Img
                                                        alt={videoItem?.title}
                                                        loading="lazy"
                                                        src={
                                                            BRAND_KEY === 'nutribullet'
                                                                ? 'https://dam.delonghigroup.com/800x640/assets/339098'
                                                                : getAssetUrl(
                                                                      'static/img/thumbnail.webp',
                                                                  )
                                                        }
                                                    />
                                                    <Flex width="100%" height="100%">
                                                        <PlayIcon
                                                            color="background.dark"
                                                            fill="background.default"
                                                            width="50%"
                                                            height="50%"
                                                            aria-hidden="true"
                                                        />
                                                    </Flex>
                                                </>
                                            </AspectRatio>
                                        </ListItem>
                                    )
                                }

                                const selected = itemIndex === selectedIndex
                                return (
                                    <ListItem
                                        key={itemIndex}
                                        height="fit-content"
                                        flexShrink={0}
                                        opacity={isModalOpen && !selected ? 0.6 : 1}
                                        sx={{_hover: {opacity: 1, cursor: 'pointer'}}}
                                    >
                                        <ProductThumbnail
                                            image={image}
                                            imageRef={imageRef}
                                            handleSelection={(index) => handleSelection(index)}
                                            index={itemIndex}
                                            selected={selected}
                                            loadingStrategy={loadingStrategy}
                                            borderRadius={isModalOpen ? 'large-1' : 'large'}
                                        />
                                    </ListItem>
                                )
                            })}
                            {showModalOverflowTile && remainingModalThumbnails > 0 && (
                                <ListItem height="fit-content">
                                    <Flex
                                        as="button"
                                        type="button"
                                        {...styles.modalOverflowTile}
                                        width={modalThumbnailSize}
                                        height={modalThumbnailSize}
                                        onClick={() => setShowAllModalThumbnails(true)}
                                        aria-label={intl.formatMessage(messages.showMoreImages, {
                                            count: remainingModalThumbnails,
                                        })}
                                    >
                                        +{remainingModalThumbnails}
                                    </Flex>
                                </ListItem>
                            )}
                        </List>
                    </Flex>
                </Flex>
                <Flex
                    as="button"
                    type="button"
                    {...styles.thumbnailImagesUpDownArrows}
                    aria-label={intl.formatMessage(messages.downArrow)}
                    {...handleChevronStyles(
                        'vertical',
                        thumbnailImages.length,
                        'bottom',
                        updatedScrollPosition,
                    )}
                    onClick={() => handleVerticalScroll(imageRefScroll / 2 + 8)}
                    tabIndex={0}
                    data-testid="thumbnail-down-arrow"
                >
                    <ChevronDown aria-hidden="true" />
                </Flex>
                {video && video?.videolink && !isModalOpen && (
                    <>
                        <Flex {...styles.dividerContainer} {...styles.mobileMargin}>
                            <ImageGalleryDivider />
                        </Flex>
                        <Flex {...styles.mobileMargin}>
                            <AspectRatio
                                as="button"
                                type="button"
                                ratio={1}
                                {...styles.thumbnailImageSkeleton}
                                borderRadius="large"
                                overflow="hidden"
                                aria-label={intl.formatMessage(messages.playVideo)}
                                data-testid="play-button-thumbnail"
                                onClick={() => {
                                    openModal(video?.videolink)
                                    setIsModalGallerySwiper(false)
                                    setSelectedIndex(videoIndex)
                                    stopPropagation()
                                    swiperRef &&
                                        swiperRef.current &&
                                        swiperRef.current.slideTo(videoIndex)
                                }}
                            >
                                <>
                                    <Img
                                        alt={video?.title}
                                        loading="lazy"
                                        src={
                                            BRAND_KEY === 'nutribullet'
                                                ? 'https://dam.delonghigroup.com/800x640/assets/339098'
                                                : BRAND_KEY === 'kenwood'
                                                ? 'https://dam.delonghigroup.com/902x902/assets/379861'
                                                : BRAND_KEY === 'braun'
                                                ? 'https://dam.delonghigroup.com/902x902/assets/380997'
                                                : getAssetUrl('static/img/thumbnail.webp')
                                        }
                                    />
                                    <Flex width="100%" height="100%">
                                        <PlayIcon
                                            color="background.dark"
                                            fill="background.default"
                                            width="50%"
                                            height="50%"
                                            aria-hidden="true"
                                        />
                                    </Flex>
                                </>
                            </AspectRatio>
                        </Flex>
                    </>
                )}
                {is360Enabled && (
                    <Flex {...styles.icon360}>
                        <Icon360 width={remCalc(40)} />
                    </Flex>
                )}
            </Flex>
        )
    },
)

GallerySlider.displayName = 'GallerySlider'

GallerySlider.propTypes = {
    scrollRef: PropTypes.object,
    swiperRef: PropTypes.object,
    loadingStrategy: PropTypes.string,
    videoUrl: PropTypes.string,
    styles: PropTypes.object,
    scrollPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    setScrollPosition: PropTypes.func,
    selectedIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    setSelectedIndex: PropTypes.func,
    thumbnailImages: PropTypes.array,
    stopPropagation: PropTypes.func,
    openModal: PropTypes.func,
    setIsModalGallerySwiper: PropTypes.func,
    isModalOpen: PropTypes.bool,
    modalInitValue: PropTypes.number,
}

export default GallerySlider
